import { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { Form, Button, Modal, Toast, ToastContainer } from 'react-bootstrap';
import { Pencil, Plus, Trash, ListTask } from 'react-bootstrap-icons';
import Swal from 'sweetalert2';
import Select from 'react-select';
import api from '../api';

const PantallasPage = () => {
  const userRole = localStorage.getItem('role');
  const salaId = parseInt(localStorage.getItem('id_sala'), 10);

  const [pantallas, setPantallas] = useState([]);
  const [filteredPantallas, setFilteredPantallas] = useState([]);
  const [serviciosDisponibles, setServiciosDisponibles] = useState([]);
  const [serviciosEspDisponibles, setServiciosEspDisponibles] = useState([]);

  const [selectedPantalla, setSelectedPantalla] = useState(null);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showServiciosModal, setShowServiciosModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingServicios, setIsSavingServicios] = useState(false);

  const [serviciosAsignados, setServiciosAsignados] = useState([]);
  const [servicioSeleccionado, setServicioSeleccionado] = useState(null);

  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [errors, setErrors] = useState({});

  const mostrarToast = (variant, message) => {
    setToast({ show: true, variant, message });
    setTimeout(() => setToast({ ...toast, show: false }), 3000);
  };

  /* ===============================
     CARGA INICIAL
  =============================== */
  useEffect(() => {
    cargarPantallas();
    api.get('/servicios').then(res => setServiciosDisponibles(res.data));
    api.get('/ventanillas/especiales').then(res => setServiciosEspDisponibles(res.data));
  }, []);

  const cargarPantallas = () => {
    setPantallas([]);
    api.get('/pantallas', { params: { sala_id: salaId } })
      .then(res => {
        setPantallas(res.data);
        setFilteredPantallas(res.data);
      })
      .catch(() => mostrarToast('danger', 'Error al cargar pantallas'));
  };

  useEffect(() => {
    const term = search.toLowerCase().trim();

    setFilteredPantallas(
      pantallas.filter(p => {
        const matchNombre = p.nombre?.toLowerCase().includes(term);
        const matchId = p.id?.toString().includes(term);

        return matchNombre || matchId;
      })
    );
  }, [search, pantallas]);

  /* ===============================
     CRUD PANTALLA
  =============================== */
  const handleAdd = () => {
    setIsEditing(false);
    setErrors({});
    setSelectedPantalla({
      nombre: '',
      descripcion: '',
      activo: 1,
      sala_id: salaId
    });
    setShowModal(true);
  };

  const handleEdit = (row) => {
    setIsEditing(true);
    setErrors({});
    setSelectedPantalla({ ...row });
    setShowModal(true);
  };

  const validate = () => {
    const e = {};
    if (!selectedPantalla.nombre?.trim()) e.nombre = 'Campo requerido';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const savePantalla = () => {
    if (!validate()) return;
    setIsSaving(true);

    const req = isEditing
      ? api.put(`/pantallas/${selectedPantalla.id}`, selectedPantalla)
      : api.post('/pantallas', selectedPantalla);

    req.then(() => {
      cargarPantallas();
      setShowModal(false);
      mostrarToast('success', `Pantalla ${isEditing ? 'actualizada' : 'creada'} correctamente`);
    })
      .catch(() => mostrarToast('danger', 'Error al guardar'))
      .finally(() => setIsSaving(false));
  };

  const eliminarPantalla = async (id) => {
    const result = await Swal.fire({
      title: "¿Desea eliminar la pantalla?",
      text: "Está seguro de eliminar la pantalla? la información y relaciones se perderan.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#0d6efd",   // Bootstrap primary
      cancelButtonColor: "#6c757d",    // Bootstrap secondary
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    api.delete(`/pantallas/${id}`)
      .then(() => {
        cargarPantallas();
        mostrarToast('success', 'Pantalla eliminada');
      })
      .catch(() => mostrarToast('danger', 'Error al eliminar'));
  };

  /* ===============================
     RELACIÓN SERVICIOS
  =============================== */
  const openServicios = (row) => {
    setSelectedPantalla(row);

    const servicios = (row.servicios || []).map(s => ({
      value: s.idServicio,
      label: s.Servicio,
      tipo: 'servicio',
      visible: s.visible
    }));

    const especiales = (row.ventanillas_especiales || []).map(v => ({
      value: v.ventanilla_id,
      label: v.ventanilla?.Servicio + '-' + v.ventanilla?.Ventanilla,
      tipo: 'especial'
    }));

    setServiciosAsignados([...servicios, ...especiales]);
    setServicioSeleccionado(null);
    setShowServiciosModal(true);
  };

  const guardarServicios = () => {
    setIsSavingServicios(true);
    const servicios = serviciosAsignados
      .filter(i => i.tipo === 'servicio')
      .map(i => i.value);

    const especiales = serviciosAsignados
      .filter(i => i.tipo === 'especial')
      .map(i => i.value);

    api.post(`/pantallas/${selectedPantalla.id}/asignaciones`, {
      servicios,
      ventanillas: especiales
    })
      .then(() => {
        cargarPantallas();
        setShowServiciosModal(false);
        mostrarToast('success', 'Servicios asignados correctamente');
      })
      .catch(() => mostrarToast('danger', 'Error al guardar'))
      .finally(() => { setIsSavingServicios(false); });
  };

  const opcionesServicios = serviciosDisponibles.map(servicio => ({
    value: servicio.idServicio,
    label: servicio.Servicio,
    tipo: 'servicio',
    visible: servicio.visible
  }));

  const opcionesEspeciales = serviciosEspDisponibles.map(servicioEsp => ({
    value: servicioEsp.id,
    label: servicioEsp.Servicio + '-' + servicioEsp.Ventanilla,
    tipo: 'especial'
  }));

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.data.tipo === 'especial' ? '#f1ef78ff' : state.data.visible === 1 ? '#d4edda' : '#f8d7da',
      color: '#000'
    })
  };

  const opcionesSelector = [
    ...opcionesServicios,
    ...opcionesEspeciales
  ];

  const agregarServicio = () => {
    if (!servicioSeleccionado) return;

    const yaExiste = serviciosAsignados.some(
      s => s.value === servicioSeleccionado.value
        && s.tipo === servicioSeleccionado.tipo
    );

    if (yaExiste) return;

    setServiciosAsignados([
      ...serviciosAsignados,
      servicioSeleccionado
    ]);

    setServicioSeleccionado(null);
  };

  const asignarTodos = () => {
    const all = [...serviciosAsignados, ...opcionesServicios, ...opcionesEspeciales];

    const unique = [];
    const seen = new Set();

    for (const item of all) {
      const key = `${item.tipo}-${item.value}`;
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(item);
      }
    }

    setServiciosAsignados(unique);
  };

  const quitarServicio = (value, tipo) => {
    setServiciosAsignados(
      serviciosAsignados.filter(
        s => !(s.value === value && s.tipo === tipo)
      )
    );
  };

  /* ===============================
     COLUMNAS
  =============================== */
  const columns = [
    { name: 'Id Pantalla', selector: row => row.id, sortable: true },
    { name: 'Nombre', selector: row => row.nombre, sortable: true },
    { name: 'Servicios',
      selector: row => {
        const normales = row.servicios?.length || 0;
        const especiales = row.ventanillas_especiales?.length || 0;
        return normales + especiales;
      },
    },
    {
      name: 'Opciones',
      cell: row => (
        <div className="d-flex gap-2">
          {userRole !== 'consulta' && (
            <>
              <Button size="sm" variant="outline-primary" onClick={() => handleEdit(row)}>
                <Pencil />
              </Button>
              <Button size="sm" variant="outline-danger" onClick={() => eliminarPantalla(row.id)}>
                <Trash />
              </Button>
            </>
          )}
          <Button size="sm" variant="outline-secondary" onClick={() => openServicios(row)}>
            <ListTask />
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="mt-4">
      <DataTable
        title="Pantallas"
        columns={columns}
        data={filteredPantallas}
        pagination
        subHeader
        subHeaderComponent={
          <div className="d-flex justify-content-between w-100">
            {userRole !== 'consulta' && (
              <Button size="sm" variant="outline-success" onClick={handleAdd}>
                Nueva <Plus />
              </Button>
            )}
            <Form.Control
              className="w-25"
              placeholder="Buscar..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        }
      />

      {/* Modal Pantalla */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? 'Editar Pantalla' : 'Nueva Pantalla'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nombre</Form.Label>
              <Form.Control
                value={selectedPantalla?.nombre || ''}
                onChange={e => setSelectedPantalla({ ...selectedPantalla, nombre: e.target.value })}
                isInvalid={!!errors.nombre}
              />
              <Form.Control.Feedback type="invalid">{errors.nombre}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group>
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                value={selectedPantalla?.descripcion || ''}
                onChange={e => setSelectedPantalla({ ...selectedPantalla, descripcion: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
          <Button variant="primary" onClick={savePantalla} disabled={isSaving}>
            {isSaving ? 'Guardando...' : 'Guardar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Servicios */}
      <Modal
        show={showServiciosModal}
        onHide={() => !isSavingServicios && setShowServiciosModal(false)}
        backdrop={isSavingServicios ? 'static' : true}
        keyboard={!isSavingServicios}
        size='xl'
      >
        <Modal.Header closeButton>
          <Modal.Title>Servicios – {selectedPantalla?.nombre}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="row">
            {/* COLUMNA IZQUIERDA */}
            <div className="col-md-4 border-end">
              <Form.Group className="mb-3">
                <Form.Label>Servicio</Form.Label>
                <Select
                  options={opcionesSelector}
                  styles={customStyles}
                  value={servicioSeleccionado}
                  onChange={setServicioSeleccionado}
                />
              </Form.Group>

              <div className="d-flex gap-2 mb-3">
                <Button variant="primary" onClick={agregarServicio}>
                  Agregar
                </Button>

                <Button variant="outline-secondary" onClick={asignarTodos}>
                  Asignar todos
                </Button>
              </div>

              {/* LEYENDA */}
              <div className="mt-4">
                <div className="d-flex align-items-center mb-2">
                  <span style={{
                    width: 12, height: 12, borderRadius: '50%',
                    backgroundColor: '#d4edda', marginRight: 8
                  }} />
                  Servicios visibles
                </div>
                <div className="d-flex align-items-center mb-2">
                  <span style={{
                    width: 12, height: 12, borderRadius: '50%',
                    backgroundColor: '#f8d7da', marginRight: 8
                  }} />
                  Servicios internos
                </div>
                <div className="d-flex align-items-center">
                  <span style={{
                    width: 12, height: 12, borderRadius: '50%',
                    backgroundColor: '#f1ef78ff', marginRight: 8
                  }} />
                  Servicios especiales
                </div>
              </div>
            </div>

            {/* COLUMNA DERECHA */}
            <div className="col-md-8">
              <h5>Servicios asignados</h5>

              {serviciosAsignados.length === 0 ? (
                <p>No hay servicios asignados.</p>
              ) : (
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Servicio</th>
                      <th>Tipo</th>
                      <th>Opciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serviciosAsignados.map((servicio, index) => (
                      <tr key={`${servicio.tipo}-${servicio.value}-${index}`}>
                        <td>{servicio.label}</td>
                        <td>
                          {servicio.tipo === 'especial'
                            ? 'Servicio especial'
                            : 'Servicio'}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => quitarServicio(servicio.value, servicio.tipo)}
                          >
                            <Trash />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" disabled={isSavingServicios} onClick={() => setShowServiciosModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={guardarServicios}
            disabled={isSavingServicios}
          >
            {isSavingServicios ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="top-end">
        <Toast bg={toast.variant} show={toast.show} onClose={() => setToast({ ...toast, show: false })}>
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default PantallasPage;
